import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  closeDeal,
  submitDealForWin,
  type CloseReasonCategory,
  type Deal,
  type DealWinSubmitPayload,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { LogDealProgressModal } from "@/components/deals/LogDealProgressModal";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { dealToasts, useToast } from "@/components/ui/toast";
import { FormError } from "@/components/ui/form-error";
import { CLOSE_REASON_LABELS } from "@/lib/deal-labels";

// Order shown in the "Reason for closure" dropdown; "other" requires a note.
const CLOSE_REASONS: CloseReasonCategory[] = [
  "lost_to_competitor",
  "project_cancelled",
  "no_progress",
  "other",
];

const INPUT_CLASS =
  "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

/** Hold the success toast on screen before finishing (CHP-234, mirrors the
 * review flows' 1.5-second hold). */
const REDIRECT_HOLD_MS = 1500;

interface Props {
  deal: Deal;
  /** Called after a successful action (post toast + 1.5s hold). Drawer → close;
   * standalone route → navigate to the deals dashboard (CHP-234). */
  onDone: () => void;
  /** Grid Quick Action that opened the host view — auto-opens the matching
   * confirm dialog on mount (deals gap-build W2). The dialogs and mutations
   * are the same ones the buttons below drive. */
  initialAction?: "submit_win" | "close_lost" | null;
  /** CHP-429: appended at the END of the action row (after Mark as closed-lost)
   * — the host passes the "Request extension" action here so it reads last. */
  trailing?: React.ReactNode;
}

/**
 * Live-deal owner actions (CHP-234 / CHP-429): a highlighted "Log deal
 * progress" (→ activity log), Request Closed-Won (→ win-submit), Mark as
 * Closed-Lost (→ close), and a trailing "Request extension" slot. The win/close
 * dialogs confirm, toast, hold, then finish.
 */
export function DealWinActions({ deal, onDone, initialAction, trailing }: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [winOpen, setWinOpen] = React.useState(false);
  const [winNotes, setWinNotes] = React.useState("");
  const [closeOpen, setCloseOpen] = React.useState(false);
  const [closeReason, setCloseReason] = React.useState<CloseReasonCategory>("lost_to_competitor");
  const [closeNotes, setCloseNotes] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  // CHP-429: "Log deal progress" modal (replaces the meeting-reward request).
  const [progressOpen, setProgressOpen] = React.useState(false);

  // Auto-open the dialog the grid Quick Action asked for. Status is gated
  // below (live only) — a stale grid row that quick-actioned a no-longer-live
  // deal renders nothing, so no dialog can open against an illegal state.
  React.useEffect(() => {
    if (deal.deal_status !== "live") return;
    if (initialAction === "submit_win") {
      setWinOpen(true);
    } else if (initialAction === "close_lost") {
      setCloseReason("lost_to_competitor");
      setCloseNotes("");
      setCloseOpen(true);
    }
  }, [initialAction, deal.deal_status]);

  const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
    },
    [],
  );

  const holdThenDone = React.useCallback(() => {
    holdTimer.current = setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      onDone();
    }, REDIRECT_HOLD_MS);
  }, [onDone, queryClient]);

  const winSubmit = useMutation({
    mutationFn: (payload: DealWinSubmitPayload) => submitDealForWin(accessToken!, deal.id, payload),
    onSuccess: () => {
      setWinOpen(false);
      toast(dealToasts.winRequested());
      holdThenDone();
    },
    onError: () => setError("Request failed. Refresh and try again."),
  });

  const close = useMutation({
    mutationFn: () =>
      closeDeal(accessToken!, deal.id, {
        close_reason_category: closeReason,
        close_notes: closeNotes.trim() || null,
      }),
    onSuccess: () => {
      setCloseOpen(false);
      // CHP-361: BDM/PO close uses the dedicated "Status updated to Closed-Lost"
      // toast (the manager-direct close keeps `closedLost`).
      toast(dealToasts.statusClosedLost());
      holdThenDone();
    },
    onError: () => setError("Close failed. Refresh and try again."),
  });

  const anyPending = winSubmit.isPending || close.isPending;

  if (deal.deal_status !== "live") return null;

  const additionalDetailsRequired = closeReason === "other";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {/* CHP-429: "Log deal progress" is the FIRST live-deal action and is
            highlighted (primary). It opens the activity-log modal. */}
        <Button
          type="button"
          onClick={() => {
            setError(null);
            setProgressOpen(true);
          }}
          disabled={anyPending}
          data-testid="log-deal-progress"
        >
          Log deal progress
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setError(null);
            setWinNotes("");
            setWinOpen(true);
          }}
          disabled={anyPending}
        >
          Request closed-won status
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setError(null);
            setCloseReason("lost_to_competitor");
            setCloseNotes("");
            setCloseOpen(true);
          }}
          disabled={anyPending}
        >
          Mark as closed-lost
        </Button>
        {/* CHP-429: "Request extension" is moved to the END of the action row. */}
        {trailing}
      </div>

      {/* CHP-429: "Log deal progress" activity-log modal (replaces the
          meeting-reward request). Shared with the Deal Progress section. */}
      <LogDealProgressModal deal={deal} open={progressOpen} onOpenChange={setProgressOpen} />

      {/* Closed-Won submission (CHP-316): identical form for BDM and PO — no
          role branching, and the "Partner estimated value" field was removed so
          both see the same box/layout/language (just an optional context note).
          The backend payload field stays optional for back-compat; the form no
          longer sends it. */}
      <Dialog open={winOpen} onOpenChange={setWinOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            {/* CHP-404: BDM/PO closed-won confirm heading. */}
            <DialogTitle>Mark as closed-won?</DialogTitle>
            {/* CHP-607: Flow E MUST copy restored verbatim (ruling 2026-07-09 —
                no designer override was ever recorded for the CHP-342 shorter
                wording). "PO" spelt out per the mockup. */}
            <DialogDescription>
              This submission is unofficial until verified by management. Your deal status will be
              officially accepted and updated once a physical Purchase Order (PO) has been received
              and successfully validated by our administrative team.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1">
            <Label htmlFor="win-context">Notes (optional)</Label>
            <Textarea
              id="win-context"
              value={winNotes}
              onChange={(e) => setWinNotes(e.target.value)}
              maxLength={1000}
              rows={3}
            />
          </div>

          {/* CHP-804: render the failure INSIDE the portalled dialog (above the
              footer) — a body-level alert is hidden behind the dialog overlay
              while the dialog is open, so a failed submit looked silent. */}
          {error ? <FormError>{error}</FormError> : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWinOpen(false)}
              disabled={winSubmit.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setError(null);
                winSubmit.mutate({ notes: winNotes.trim() || null });
              }}
              loading={winSubmit.isPending}
              loadingLabel="Updating…"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Closed-Lost needs a dropdown + conditional note, richer than ConfirmDialog. */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            {/* CHP-404: BDM/PO closed-lost confirm heading. */}
            <DialogTitle>Mark as closed-lost?</DialogTitle>
            <DialogDescription>
              This deal registration will be deactivated upon confirmation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1">
            <Label htmlFor="close-reason">Reason for closure *</Label>
            <select
              id="close-reason"
              value={closeReason}
              onChange={(e) => setCloseReason(e.target.value as CloseReasonCategory)}
              className={INPUT_CLASS}
            >
              {CLOSE_REASONS.map((r) => (
                <option key={r} value={r}>
                  {CLOSE_REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="close-notes">
              Notes{additionalDetailsRequired ? " *" : " (Optional)"}
            </Label>
            <Textarea
              id="close-notes"
              value={closeNotes}
              onChange={(e) => setCloseNotes(e.target.value)}
              maxLength={1000}
              rows={3}
            />
            {/* CHP-343: spell out the conditional requirement. */}
            <p className="text-xs text-muted-foreground">
              Required if &apos;Other&apos; is selected.
            </p>
          </div>

          {/* CHP-804: failure rendered inside the portalled dialog (see above). */}
          {error ? <FormError>{error}</FormError> : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCloseOpen(false)}
              disabled={close.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setError(null);
                close.mutate();
              }}
              disabled={additionalDetailsRequired && closeNotes.trim().length === 0}
              loading={close.isPending}
              loadingLabel="Updating…"
            >
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
